/**
 * Fortifire mobile product carousel
 * --------------------------------
 * Builds a horizontal scroll-snap carousel on viewports ≤767px for the
 * .prodMobSlider element rendered by sections/blanket-product.liquid.
 *
 * The layout is applied via inline styles with the `!important` flag so it
 * overrides any conflicting selector in ta_styles.css, base.css, etc.
 *
 * Behaviour:
 *   - flex/row .prodMobTrack with overflow-x:auto + scroll-snap-type:x mandatory
 *   - each .prodMobSlide is calc(100% - 32px) wide, leaving a 32px peek of the
 *     next slide on the right
 *   - .prodMobDots is populated with one button per slide; the active dot
 *     elongates and turns dark blue, follows scroll position, and is clickable
 *   - on desktop (>767px) the slider is hidden; on resize across the breakpoint
 *     the carousel is re-synchronised.
 *
 * Public API: window.FortifireMobileCarousel.refresh() — re-runs setup; useful
 * if the markup is replaced (e.g. theme editor section reload).
 */
(function () {
  'use strict';

  var BREAKPOINT = 767;
  var SEL_SLIDER = '.prodMobSlider';
  var SEL_TRACK = '.prodMobTrack';
  var SEL_SLIDE = '.prodMobSlide';
  var SEL_DOTS = '.prodMobDots';
  var CLASS_DOT = 'prodMobDot';
  var INIT_ATTR = 'data-fmc-init';

  var ACTIVE_BG = '#1c3766';
  var INACTIVE_BG = '#d8d8d8';
  var PEEK_PX = 32;
  var GAP_PX = 12;

  function isMobile() {
    return window.innerWidth <= BREAKPOINT;
  }

  function setStyles(el, styles) {
    if (!el) return;
    for (var key in styles) {
      if (Object.prototype.hasOwnProperty.call(styles, key)) {
        el.style.setProperty(key, styles[key], 'important');
      }
    }
  }

  function clearStyles(el, keys) {
    if (!el) return;
    for (var i = 0; i < keys.length; i++) {
      el.style.removeProperty(keys[i]);
    }
  }

  function styleDot(dotEl, active) {
    setStyles(dotEl, {
      'width': active ? '22px' : '8px',
      'height': '8px',
      'border-radius': active ? '4px' : '50%',
      'border': '0',
      'background': active ? ACTIVE_BG : INACTIVE_BG,
      'padding': '0',
      'cursor': 'pointer',
      'transition': 'width 160ms ease, background 160ms ease, border-radius 160ms ease',
      'flex': '0 0 auto',
      'outline': 'none',
      'appearance': 'none',
      '-webkit-appearance': 'none'
    });
  }

  function injectGlobalStyle() {
    if (document.getElementById('fmc-global-style')) return;
    var s = document.createElement('style');
    s.id = 'fmc-global-style';
    s.textContent =
      '.prodMobTrack::-webkit-scrollbar{display:none!important;width:0!important;height:0!important;}' +
      '.prodMobTrack{-ms-overflow-style:none!important;}' +
      '.prodMobDot:focus-visible{outline:2px solid ' + ACTIVE_BG + ';outline-offset:2px;}';
    document.head.appendChild(s);
  }

  function buildDots(dotsWrap, count) {
    dotsWrap.innerHTML = '';
    setStyles(dotsWrap, {
      'display': 'flex',
      'gap': '6px',
      'align-items': 'center',
      'justify-content': 'center',
      'padding': '14px 16px 4px',
      'margin': '0',
      'list-style': 'none',
      'width': '100%',
      'box-sizing': 'border-box'
    });
    for (var i = 0; i < count; i++) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = CLASS_DOT + (i === 0 ? ' active' : '');
      btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      btn.setAttribute('data-i', String(i));
      styleDot(btn, i === 0);
      dotsWrap.appendChild(btn);
    }
  }

  function applyLayout(slider, track, slides) {
    setStyles(slider, {
      'display': 'block',
      'width': '100%',
      'max-width': '100%',
      'margin': '0',
      'padding': '0',
      'box-sizing': 'border-box',
      'position': 'relative',
      'float': 'none'
    });

    setStyles(track, {
      'display': 'flex',
      'flex-direction': 'row',
      'flex-wrap': 'nowrap',
      'overflow-x': 'auto',
      'overflow-y': 'hidden',
      'scroll-snap-type': 'x mandatory',
      'scroll-behavior': 'smooth',
      '-webkit-overflow-scrolling': 'touch',
      'scrollbar-width': 'none',
      'width': '100%',
      'max-width': '100%',
      'margin': '0',
      'padding': '0',
      'gap': GAP_PX + 'px',
      'box-sizing': 'border-box',
      'float': 'none'
    });

    // Slide width factors in the gap so the next-slide peek stays at PEEK_PX.
    var slideWidth = 'calc(100% - ' + (PEEK_PX + GAP_PX) + 'px)';
    for (var i = 0; i < slides.length; i++) {
      setStyles(slides[i], {
        'flex': '0 0 ' + slideWidth,
        'width': slideWidth,
        'max-width': slideWidth,
        'min-width': slideWidth,
        'aspect-ratio': '1 / 1',
        'scroll-snap-align': 'start',
        'scroll-snap-stop': 'always',
        'height': 'auto',
        'box-sizing': 'border-box',
        'float': 'none',
        'position': 'relative',
        'background': '#f5f5f5',
        'border-radius': '0',
        'overflow': 'hidden'
      });

      var imgs = slides[i].querySelectorAll('img');
      for (var j = 0; j < imgs.length; j++) {
        setStyles(imgs[j], {
          'width': '100%',
          'height': '100%',
          'display': 'block',
          'margin': '0',
          'border-radius': '0',
          'object-fit': 'contain',
          'object-position': 'center'
        });
      }
    }
  }

  function bindDots(slider, track, slides, dotsWrap) {
    if (!dotsWrap) return;
    if (slides.length <= 1) {
      dotsWrap.style.setProperty('display', 'none', 'important');
      return;
    }

    buildDots(dotsWrap, slides.length);

    function getStep() {
      if (slides.length < 2) return slides[0].offsetWidth || track.offsetWidth || 1;
      var rect0 = slides[0].getBoundingClientRect();
      var rect1 = slides[1].getBoundingClientRect();
      var step = rect1.left - rect0.left;
      return step || slides[0].offsetWidth || track.offsetWidth || 1;
    }

    var rafPending = false;
    function updateDots() {
      rafPending = false;
      var step = getStep();
      if (!step) return;
      var idx = Math.round(track.scrollLeft / step);
      if (idx < 0) idx = 0;
      if (idx > slides.length - 1) idx = slides.length - 1;
      var dots = dotsWrap.querySelectorAll('.' + CLASS_DOT);
      for (var k = 0; k < dots.length; k++) {
        var active = (k === idx);
        if (active) dots[k].classList.add('active');
        else dots[k].classList.remove('active');
        styleDot(dots[k], active);
      }
    }

    track.addEventListener('scroll', function () {
      if (rafPending) return;
      rafPending = true;
      window.requestAnimationFrame(updateDots);
    }, { passive: true });

    dotsWrap.addEventListener('click', function (e) {
      var node = e.target;
      while (node && node !== dotsWrap && !(node.classList && node.classList.contains(CLASS_DOT))) {
        node = node.parentNode;
      }
      if (!node || !node.classList || !node.classList.contains(CLASS_DOT)) return;
      var idx = parseInt(node.getAttribute('data-i'), 10) || 0;
      track.scrollTo({ left: idx * getStep(), behavior: 'smooth' });
    });
  }

  function setup(slider) {
    var track = slider.querySelector(SEL_TRACK);
    if (!track) return false;
    var slides = track.querySelectorAll(SEL_SLIDE);
    if (slides.length === 0) return false;
    var dotsWrap = slider.querySelector(SEL_DOTS);

    injectGlobalStyle();
    applyLayout(slider, track, slides);
    bindDots(slider, track, slides, dotsWrap);

    slider.setAttribute(INIT_ATTR, '1');
    return true;
  }

  function hideOnDesktop(slider) {
    setStyles(slider, { 'display': 'none' });
  }

  function showAndRestore(slider) {
    var track = slider.querySelector(SEL_TRACK);
    if (!track) return;
    var slides = track.querySelectorAll(SEL_SLIDE);
    if (slides.length === 0) return;
    applyLayout(slider, track, slides);
  }

  function sync() {
    var slider = document.querySelector(SEL_SLIDER);
    if (!slider) return;

    if (isMobile()) {
      if (slider.hasAttribute(INIT_ATTR)) {
        showAndRestore(slider);
      } else {
        setup(slider);
      }
    } else {
      hideOnDesktop(slider);
    }
  }

  function refresh() {
    var slider = document.querySelector(SEL_SLIDER);
    if (slider) slider.removeAttribute(INIT_ATTR);
    sync();
  }

  function init() {
    sync();

    var lastMobile = isMobile();
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      var nowMobile = isMobile();
      if (nowMobile === lastMobile) return;
      lastMobile = nowMobile;
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(sync, 80);
    });

    // Re-init when the section is re-rendered in the Shopify theme editor.
    document.addEventListener('shopify:section:load', refresh);
    document.addEventListener('shopify:section:unload', refresh);
  }

  window.FortifireMobileCarousel = { init: init, sync: sync, refresh: refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
